import { Layout } from "@/components/layout";
import {
  useListAccounts,
  useListPhoneTypes,
  useBulkTestPhones,
  useTestAccountPhone,
  useListRecentPhoneTests,
  useListBuybackAccounts,
  useRefreshBuybackAccounts,
  getBuybackRefreshTargets,
  getListAccountsQueryKey,
  getListRecentPhoneTestsQueryKey,
  getListBuybackAccountsQueryKey,
  type Account,
  type ListAccountsPhoneStatus,
  type ListAccountsPhoneType,
} from "@workspace/api-client-react";
import { Loader2, Send, History, List, LayoutGrid, RefreshCw, Download } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useSearch } from "wouter";
import { useQueryClient } from "@tanstack/react-query";
import type { RowSelectionState } from "@tanstack/react-table";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { usePullerFilter } from "@/contexts/puller-filter";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import {
  AccountsTable,
  accountHealthState,
  type HealthState,
} from "@/components/accounts-table";
import { AccountsGrouped, type PhoneMode } from "@/components/accounts-grouped";
import { BuybackAccountsTable } from "@/components/buyback-accounts-table";
import { PhoneTestsTable } from "@/components/phone-tests-table";
import { toast } from "sonner";
import {
  buildAccountsExportRows,
  accountsExportFilename,
  exportToCsv,
} from "@/lib/csv-export";

type PhoneTypeFilter = "all" | ListAccountsPhoneType;
type HealthFilter = "all" | ListAccountsPhoneStatus;
type AccountsView = "list" | "group";
type AccountCategory = "source" | "buyback";

// Cap each single-puller refresh request. On Autoscale a request can stall past
// any useful wait (the original bug: the whole run froze at "0/N" because one
// request never returned), so we time each puller out, count it as failed, and
// let the loop advance instead of hanging the button forever.
const REFRESH_PULLER_TIMEOUT_MS = 60_000;

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(
      () => reject(new Error("buyback refresh request timed out")),
      ms,
    );
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (err) => {
        clearTimeout(timer);
        reject(err);
      },
    );
  });
}

export default function Accounts() {
  const [search, setSearch] = useState("");
  // Deep-link from the Emails drawer: `/accounts?q=<email>` seeds the search.
  const routeSearch = useSearch();
  useEffect(() => {
    const q = new URLSearchParams(routeSearch).get("q");
    if (q) setSearch(q);
  }, [routeSearch]);
  const [category, setCategory] = useState<AccountCategory>("source");
  const [phoneType, setPhoneType] = useState<PhoneTypeFilter>("all");
  const [health, setHealth] = useState<HealthFilter>("all");
  const [view, setView] = useState<AccountsView>("list");
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  // Collapsed puller buckets in Group view, keyed by puller name. Kept at the
  // page level (not inside AccountsGrouped) so the state survives toggling
  // between List and Group view for the operator's session.
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(
    () => new Set(),
  );
  const toggleGroupCollapsed = (key: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };
  const [bulkTesting, setBulkTesting] = useState(false);
  const [activityOpen, setActivityOpen] = useState(false);
  const qc = useQueryClient();

  // The puller filter is global (header dropdown). Scoping is applied server-side
  // via the pullerId param so a pending request shows the loading spinner instead
  // of a misleading empty table while data is in-flight.
  const { pullerId } = usePullerFilter();
  const { data: accounts, isLoading } = useListAccounts({
    search: search || undefined,
    phoneType: phoneType === "all" ? undefined : phoneType,
    phoneStatus: health === "all" ? undefined : health,
    pullerId: pullerId ?? undefined,
  });

  // Buy-back accounts are a distinct, AMP-preloaded category (never bank-linked).
  // The list is server-ranked by quality and scoped to the global puller filter;
  // search filters it client-side since the endpoint only takes a puller param.
  const isBuyback = category === "buyback";
  const { data: buybackData, isLoading: buybackLoading } = useListBuybackAccounts(
    { pullerId: pullerId ?? undefined },
    {
      query: {
        enabled: isBuyback,
        queryKey: getListBuybackAccountsQueryKey({
          pullerId: pullerId ?? undefined,
        }),
      },
    },
  );
  const buybackAccounts = useMemo(() => {
    const rows = buybackData?.accounts ?? [];
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (a) =>
        a.vendorAccountEmail.toLowerCase().includes(q) ||
        (a.displayName ?? "").toLowerCase().includes(q) ||
        (a.phone ?? "").toLowerCase().includes(q),
    );
  }, [buybackData, search]);
  // Every refresh runs synchronously on the server (detached background work does
  // not survive on Autoscale). A single-puller refresh is one short request; an
  // all-pullers refresh is orchestrated client-side as bounded chunks so each
  // request finishes inside its response and the pool fills progressively.
  const refreshBuyback = useRefreshBuybackAccounts();
  const [refreshProgress, setRefreshProgress] = useState<{
    done: number;
    total: number;
  } | null>(null);
  const [isResolvingTargets, setIsResolvingTargets] = useState(false);
  // Airtight re-entrancy guard: `disabled` only takes effect after a re-render,
  // so two synchronous clicks could otherwise both start a refresh (duplicating
  // AMP work). The ref blocks the second call immediately.
  const refreshBusyRef = useRef(false);
  const isRefreshing =
    refreshBuyback.isPending || refreshProgress !== null || isResolvingTargets;

  async function handleRefreshBuyback() {
    if (refreshBusyRef.current) return;
    refreshBusyRef.current = true;
    try {
      // Scoped to the global puller filter: one short inline request.
      if (pullerId != null) {
        try {
          const res = await refreshBuyback.mutateAsync({ data: { pullerId } });
          toast.success(
            res.upserted > 0
              ? `Refreshed ${res.upserted} buy-back account${res.upserted === 1 ? "" : "s"}`
              : "No buy-back accounts found for this puller",
          );
          qc.invalidateQueries({ queryKey: getListBuybackAccountsQueryKey() });
        } catch {
          toast.error(
            "Couldn't refresh buy-back accounts — AMP (Zoho) may not be configured",
          );
        }
        return;
      }

      // No puller selected → refresh the whole pool one puller per request.
      let targets: number[];
      setIsResolvingTargets(true);
      try {
        targets = (await getBuybackRefreshTargets()).pullerIds;
      } catch {
        toast.error("Couldn't load the list of pullers to refresh");
        return;
      } finally {
        setIsResolvingTargets(false);
      }
      if (targets.length === 0) {
        toast.success("No pullers with inventory to refresh");
        return;
      }

      // One puller per request. Each single-puller refresh mirrors the proven
      // inline path above and stays small enough to finish inside the Autoscale
      // request window; a multi-puller batch was too heavy and never returned,
      // sticking the button at "Refreshing 0/N".
      const key = getListBuybackAccountsQueryKey();
      let upserted = 0;
      let failed = 0;
      setRefreshProgress({ done: 0, total: targets.length });
      try {
        for (let i = 0; i < targets.length; i++) {
          try {
            const res = await withTimeout(
              refreshBuyback.mutateAsync({ data: { pullerId: targets[i] } }),
              REFRESH_PULLER_TIMEOUT_MS,
            );
            upserted += res.upserted;
          } catch {
            failed += 1;
          }
          setRefreshProgress({ done: i + 1, total: targets.length });
          qc.invalidateQueries({ queryKey: key });
        }
      } finally {
        setRefreshProgress(null);
        // A puller that hit the client timeout leaves its mutation pending (we
        // abandoned the raced promise, not the underlying request). Reset so a
        // lingering `isPending` can't keep the button spinning after the run.
        refreshBuyback.reset();
      }

      const ok = targets.length - failed;
      if (failed === 0) {
        toast.success(
          `Refreshed ${upserted} buy-back account${upserted === 1 ? "" : "s"} across ${ok} puller${ok === 1 ? "" : "s"}`,
        );
      } else if (ok === 0) {
        toast.error(
          "Couldn't refresh buy-back accounts — AMP (Zoho) may not be configured",
        );
      } else {
        toast.warning(
          `Refreshed ${upserted} buy-back account${upserted === 1 ? "" : "s"} across ${ok}/${targets.length} pullers (${failed} failed)`,
        );
      }
    } finally {
      refreshBusyRef.current = false;
    }
  }

  // Map each phone type to its resolved automated/manual mode so the grouped
  // view can summarize phone-type composition without changing API fields.
  const { data: phoneTypes } = useListPhoneTypes();
  const modeFor = useMemo(() => {
    const map = new Map<string, PhoneMode>();
    for (const pt of phoneTypes ?? []) map.set(pt.phoneType, pt.mode);
    return (account: Account): PhoneMode =>
      (account.wrPhoneType && map.get(account.wrPhoneType)) || "manual";
  }, [phoneTypes]);

  // Filter changes can drop previously-selected rows from the list, so clear
  // the selection to avoid acting on rows the operator can no longer see.
  useEffect(() => {
    setRowSelection({});
  }, [search, phoneType, health, pullerId]);

  const bulkTest = useBulkTestPhones({
    mutation: {
      onSuccess: (res) => {
        toast.success(
          res.queued > 0
            ? `Queued ${res.queued} automated phone test${res.queued === 1 ? "" : "s"}`
            : "No eligible automated phones to test",
        );
        qc.invalidateQueries({ queryKey: getListAccountsQueryKey() });
        qc.invalidateQueries({ queryKey: getListRecentPhoneTestsQueryKey() });
        if (res.queued > 0) setActivityOpen(true);
      },
      onError: () => toast.error("Twilio is not configured"),
    },
  });

  const testPhone = useTestAccountPhone();

  // Health counts reflect the currently filtered set, so the strip updates live
  // as the operator changes filters or search.
  const healthCounts = useMemo(() => {
    const counts: Record<HealthState, number> = { good: 0, warning: 0, bad: 0 };
    for (const a of accounts ?? []) counts[accountHealthState(a)]++;
    return counts;
  }, [accounts]);

  const selectedAccounts = useMemo(
    () => (accounts ?? []).filter((a) => rowSelection[String(a.id)]),
    [accounts, rowSelection],
  );
  // Healthy phones can't be selected, but guard anyway so the bulk run never
  // re-tests a known-good number.
  const testableSelected = useMemo(
    () => selectedAccounts.filter((a) => a.phoneStatus !== "good" && a.wrPhone),
    [selectedAccounts],
  );

  async function testSelected() {
    if (testableSelected.length === 0) return;
    setBulkTesting(true);
    let sent = 0;
    let failed = 0;
    for (const account of testableSelected) {
      try {
        const res = await testPhone.mutateAsync({ id: account.id });
        if (res.status === "sent") sent++;
        else failed++;
      } catch {
        failed++;
      }
    }
    setBulkTesting(false);
    setRowSelection({});
    qc.invalidateQueries({ queryKey: getListAccountsQueryKey() });
    qc.invalidateQueries({ queryKey: getListRecentPhoneTestsQueryKey() });
    if (sent > 0) {
      toast.success(
        `Sent ${sent} test SMS${sent === 1 ? "" : "es"}` +
          (failed > 0 ? `, ${failed} failed` : ""),
      );
    } else {
      toast.error("No test SMS could be sent");
    }
  }

  return (
    <Layout>
      <div className="flex flex-col h-full w-full bg-background p-2 sm:p-4 overflow-hidden">
        <div className="mb-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 shrink-0">
          <div>
            <h1 className="text-lg font-medium text-foreground">Vendor Accounts</h1>
            <p className="text-xs text-muted-foreground">
              {isBuyback
                ? "Pre-loaded buy-back accounts, ranked by quality (never bank-linked)"
                : "Manage vendor accounts, phones, and juice renters"}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
            <ToggleGroup
              type="single"
              size="sm"
              value={category}
              onValueChange={(v) => v && setCategory(v as AccountCategory)}
              className="justify-start shrink-0"
            >
              <ToggleGroupItem value="source" className="h-8 px-3 text-xs">
                Source
              </ToggleGroupItem>
              <ToggleGroupItem value="buyback" className="h-8 px-3 text-xs">
                Buy-back
              </ToggleGroupItem>
            </ToggleGroup>
            {isBuyback ? (
              <Button
                size="sm"
                variant="outline"
                className="h-8 whitespace-nowrap rounded-full"
                disabled={isRefreshing}
                onClick={handleRefreshBuyback}
              >
                {isRefreshing ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <RefreshCw className="h-3.5 w-3.5 text-blue-400" />
                )}
                <span className="ml-1">
                  {refreshProgress
                    ? `Refreshing ${refreshProgress.done}/${refreshProgress.total}…`
                    : "Refresh buy-back list"}
                </span>
              </Button>
            ) : (
              <>
                {testableSelected.length > 0 && (
                  <Button
                    size="sm"
                    className="h-8 whitespace-nowrap"
                    disabled={bulkTesting}
                    onClick={testSelected}
                  >
                    {bulkTesting ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Send className="h-3.5 w-3.5" />
                    )}
                    <span className="ml-1">Test {testableSelected.length} selected</span>
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="outline"
                  className="h-8 whitespace-nowrap rounded-full"
                  disabled={bulkTest.isPending}
                  onClick={() => bulkTest.mutate()}
                >
                  {bulkTest.isPending ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Send className="h-3.5 w-3.5 text-blue-400" />
                  )}
                  <span className="ml-1">Test automated phones</span>
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-8 whitespace-nowrap rounded-full"
                  onClick={() => setActivityOpen(true)}
                >
                  <History className="h-3.5 w-3.5" />
                  <span className="ml-1">Test activity</span>
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-8 whitespace-nowrap rounded-full"
                  disabled={!accounts || accounts.length === 0}
                  title={
                    accounts && accounts.length > 0
                      ? `Export ${accounts.length} account${accounts.length === 1 ? "" : "s"} to CSV`
                      : "No accounts to export"
                  }
                  onClick={() => {
                    if (!accounts || accounts.length === 0) return;
                    exportToCsv(buildAccountsExportRows(accounts), accountsExportFilename());
                  }}
                >
                  <Download className="h-3.5 w-3.5" />
                  <span className="ml-1">Export CSV</span>
                </Button>
              </>
            )}
            <div className="w-full sm:w-72">
              <Input 
                placeholder="Search by email, name, phone..." 
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="h-8 text-sm"
              />
            </div>
          </div>
        </div>

        {!isBuyback && (
        <div className="mb-3 flex flex-wrap items-center gap-x-6 gap-y-2 shrink-0">
          <div className="flex items-center gap-2">
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-mono">
              Phone Type
            </span>
            <ToggleGroup
              type="single"
              size="sm"
              value={phoneType}
              onValueChange={(v) => v && setPhoneType(v as PhoneTypeFilter)}
              className="justify-start"
            >
              <ToggleGroupItem value="all" className="h-7 px-3 text-xs">All</ToggleGroupItem>
              <ToggleGroupItem value="automated" className="h-7 px-3 text-xs">Automated</ToggleGroupItem>
              <ToggleGroupItem value="manual" className="h-7 px-3 text-xs">Manual</ToggleGroupItem>
            </ToggleGroup>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-mono">
              Health
            </span>
            <ToggleGroup
              type="single"
              size="sm"
              value={health}
              onValueChange={(v) => v && setHealth(v as HealthFilter)}
              className="justify-start"
            >
              <ToggleGroupItem value="all" className="h-7 px-3 text-xs">All</ToggleGroupItem>
              <ToggleGroupItem value="unknown" className="h-7 px-3 text-xs">Unknown</ToggleGroupItem>
              <ToggleGroupItem value="bad" className="h-7 px-3 text-xs">Bad</ToggleGroupItem>
              <ToggleGroupItem value="good" className="h-7 px-3 text-xs">Good</ToggleGroupItem>
            </ToggleGroup>
          </div>
          <div className="flex items-center gap-2 sm:ml-auto">
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-mono">
              View
            </span>
            <ToggleGroup
              type="single"
              size="sm"
              value={view}
              onValueChange={(v) => v && setView(v as AccountsView)}
              className="justify-start"
            >
              <ToggleGroupItem value="list" className="h-7 px-2.5 text-xs" aria-label="List view">
                <List className="h-3.5 w-3.5" />
                <span className="ml-1">List</span>
              </ToggleGroupItem>
              <ToggleGroupItem value="group" className="h-7 px-2.5 text-xs" aria-label="Group view">
                <LayoutGrid className="h-3.5 w-3.5" />
                <span className="ml-1">Group</span>
              </ToggleGroupItem>
            </ToggleGroup>
          </div>
        </div>
        )}

        {!isBuyback && (
        <div className="mb-2 flex items-center gap-4 shrink-0 rounded-md border border-border bg-card px-3 py-1.5 text-xs">
          <div className="flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-full ring-2 bg-green-500 ring-green-500/30" />
            <span className="font-mono text-foreground">{healthCounts.good}</span>
            <span className="text-muted-foreground">Good</span>
          </div>
          <span className="text-border">·</span>
          <div className="flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-full ring-2 bg-amber-500 ring-amber-500/30" />
            <span className="font-mono text-foreground">{healthCounts.warning}</span>
            <span className="text-muted-foreground">Warning</span>
          </div>
          <span className="text-border">·</span>
          <div className="flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-full ring-2 bg-red-500 ring-red-500/30" />
            <span className="font-mono text-foreground">{healthCounts.bad}</span>
            <span className="text-muted-foreground">Bad</span>
          </div>
        </div>
        )}

        <div className="flex-1 border border-border rounded-md bg-card overflow-hidden flex flex-col">
          {isBuyback ? (
            buybackLoading ? (
              <div className="flex-1 flex items-center justify-center">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
              </div>
            ) : (
              <div className="flex-1 overflow-auto custom-scrollbar">
                <BuybackAccountsTable data={buybackAccounts} />
              </div>
            )
          ) : isLoading ? (
            <div className="flex-1 flex items-center justify-center">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
          ) : accounts ? (
            <div className="flex-1 overflow-auto custom-scrollbar">
              {view === "group" ? (
                <AccountsGrouped
                  data={accounts}
                  rowSelection={rowSelection}
                  onRowSelectionChange={setRowSelection}
                  modeFor={modeFor}
                  collapsed={collapsedGroups}
                  onToggleCollapsed={toggleGroupCollapsed}
                />
              ) : (
                <AccountsTable
                  data={accounts}
                  rowSelection={rowSelection}
                  onRowSelectionChange={setRowSelection}
                />
              )}
            </div>
          ) : (
            <div className="flex-1 flex items-center justify-center text-sm text-muted-foreground">
              No accounts found
            </div>
          )}
        </div>
      </div>
      <PhoneTestActivitySheet open={activityOpen} onOpenChange={setActivityOpen} />
    </Layout>
  );
}

function PhoneTestActivitySheet({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { data, isLoading } = useListRecentPhoneTests({
    query: {
      enabled: open,
      queryKey: getListRecentPhoneTestsQueryKey(),
      // Poll while a bulk run is in flight so per-account outcomes appear live.
      refetchInterval: open ? 4000 : false,
    },
  });

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-2xl overflow-y-auto custom-scrollbar"
      >
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            Phone Test Activity
            {data?.running && (
              <span className="inline-flex items-center gap-1.5 text-xs font-normal text-amber-500">
                <Loader2 className="h-3 w-3 animate-spin" />
                Bulk run in progress
              </span>
            )}
          </SheetTitle>
          <SheetDescription>
            Recent single and bulk test-SMS attempts across all accounts.
          </SheetDescription>
        </SheetHeader>
        <div className="mt-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="h-5 w-5 animate-spin text-primary" />
            </div>
          ) : (
            <PhoneTestsTable
              tests={data?.tests ?? []}
              showAccount
              emptyLabel="No recent phone-test attempts."
            />
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
